import { type components } from '@/generated/internal-api/openapi-types';
export type ReservedSampleApiId = components['schemas']['ReservedSampleApiId'];
import { type Brand, type ID } from '@/model/Common.model';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import { type StudyApiId } from '@/model/Study.model';
import {
  type GroupTarget,
  type SampleDefinitionV1,
  type PlannedAnimalTarget,
  type RegisteredAnimalTarget,
  type SampleDefinitionApiId,
  type SamplingSheetBaseColumn,
} from '@/model/SamplePlan.model';
import type { MetadataGlossaryApiId } from '@/model/Metadata.model.ts';
import { type TreatmentV1 } from '@/model/Treatment.model';
import { type DosingMatrixAnimal } from '@/components/Samples/Dosing/DosingPlan.utils';

export type StaticGroupDimension = Exclude<components['schemas']['ReservedSampleGroupDimension'], 'sampleMeta'>;

export type MetaGroupDimension = { readonly type: 'sampleMeta'; readonly apiId: MetadataGlossaryApiId };
export type GroupDimension = StaticGroupDimension | MetaGroupDimension;

export const isMetaGroupDimension = (d: GroupDimension): d is MetaGroupDimension =>
  typeof d === 'object' && d.type === 'sampleMeta';

export const DEFAULT_GROUPINGS: Array<GroupDimension> = ['sampleDefinition', 'studyGroup'];

export interface MetaField {
  apiId: MetadataGlossaryApiId;
  title: string;
}

export const STATIC_DIMENSION_LABELS: Record<StaticGroupDimension, string> = {
  sampleDefinition: 'Sample Definition',
  studyGroup: 'Study Group',
  plannedAnimal: 'Planned Animal',
  animal: 'Animal',
  status: 'Status',
  timepoint: 'Timepoint',
  sampleType: 'Sample Type',
};

export const MAX_CHIPS = 4;

export type SamplePlan = components['schemas']['SamplePlanV1'];
export type ReservedSampleAggregation = components['schemas']['ReservedSampleAggregationV1'];
export type ReservedSample = components['schemas']['ReservedSampleV1'];
export type MetadataGlossaryV1 = components['schemas']['MetadataGlossaryV1'];
export type ReservedSampleNullableDimension = components['schemas']['ReservedSampleNullableDimension'];
export type ReservedSampleSheetSort = components['schemas']['SampleSheetSort'];
export type ReservedSampleSheetSortClause = components['schemas']['SampleSheetSortClause'];

export const SHEET_SORT_FIELD_OPTIONS: Array<{ value: ReservedSampleSheetSort; label: string }> = [
  { value: 'study_group', label: 'Study group' },
  { value: 'animal_number', label: 'Animal number' },
  { value: 'animal_name', label: 'Animal name' },
];

export const SECONDARY_SORT_FIELD_OPTIONS: Array<{ value: ReservedSampleSheetSort; label: string }> = [
  { value: 'animal_number', label: 'Animal number' },
  { value: 'animal_name', label: 'Animal name' },
];

export type SampleDefinitionMap = Map<SampleDefinitionApiId, SampleDefinitionV1>;
export type GroupMap = Map<GroupApiId, TreatmentGroup>;
export type GroupByNumberMap = Map<number, TreatmentGroup>;
export type SampleTypeMap = Map<string, string>;
export type AnimalMap = Map<
  PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id'],
  ({ type: 'planned' } & PlannedAnimalTarget) | ({ type: 'registered' } & RegisteredAnimalTarget)
>;

export type ChildData<T> = { status: 'loading' } | { status: 'loaded'; data: T } | { status: 'error'; error: unknown };

export type RootKey = Brand<string, 'RootKey'>;
export type Level1Key = Brand<string, 'Level1Key'>;
export type Level2Key = Brand<string, 'Level2Key'>;

/** Brands a raw aggregation key string as a RootKey (single segment). */
export const toRootKey = (key: string): RootKey => key as RootKey;
/** Brands a two-segment compound key as a Level1Key. */
export const toLevel1Key = (rootKey: RootKey, key: string): Level1Key => `${rootKey}:${key}` as Level1Key;
/** Brands a three-segment compound key as a Level2Key. */
export const toLevel2Key = (rootKey: RootKey, level1Key: string, level2Key: string): Level2Key =>
  `${rootKey}:${level1Key}:${level2Key}` as Level2Key;

export type LeafKey = RootKey | Level1Key | Level2Key;

export type TreeStaticColumnId =
  | 'name'
  | 'group'
  | 'planned_animal'
  | 'animal'
  | 'status'
  | 'timepoint'
  | 'sample_type'
  | 'sex'
  | 'animal_number'
  | 'cage_name'
  | 'terminated_at';

const TREE_STATIC_COLUMNS = [
  { id: 'name', label: 'Sample Names', width: 'minmax(20rem, 1fr)', optIn: false },
  { id: 'group', label: 'Study Groups', width: '12rem', optIn: false },
  { id: 'planned_animal', label: 'Planned Animals', width: '8rem', optIn: false },
  { id: 'animal', label: 'Animals', width: '8rem', optIn: false },
  { id: 'status', label: 'Status', width: '10rem', optIn: false },
  { id: 'timepoint', label: 'Timepoints', width: '14rem', optIn: false },
  { id: 'sample_type', label: 'Sample Types', width: 'minmax(12rem, 2fr)', optIn: false },
  { id: 'sex', label: 'Sex', width: '6rem', optIn: true },
  { id: 'animal_number', label: 'Animal No.', width: '10rem', optIn: true },
  { id: 'cage_name', label: 'Cage Name', width: '12rem', optIn: true },
  { id: 'terminated_at', label: 'Deceased Date', width: '14rem', optIn: true },
] as const;

export const TREE_STATIC_COLUMN_DEFS = TREE_STATIC_COLUMNS as ReadonlyArray<{
  id: TreeStaticColumnId;
  label: string;
  width: string;
  optIn?: boolean;
}>;

export type SampleNamesTableContext = {
  studyApiId: StudyApiId;
  studyId: ID;
  groupMap: GroupMap;
  groupByNumberMap: GroupByNumberMap;
  animalCountByGroup: Record<string, number>;
  groupings: Array<GroupDimension>;
  metaFields: Array<MetaField>;
  sampleTypeMap: SampleTypeMap;
  animalMap: AnimalMap;
  sampleDefinitionMap: SampleDefinitionMap;
  openRoots: Set<RootKey>;
  openLevel1: Set<Level1Key>;
  openLevel2: Set<Level2Key>;
  level1Children: Map<RootKey, ChildData<Array<ReservedSampleAggregation>>>;
  level2Children: Map<Level1Key, ChildData<Array<ReservedSampleAggregation>>>;
  leafNames: Map<LeafKey, ChildData<Array<ReservedSample>>>;
  /** Static column IDs that are currently visible; opt-out (all visible unless explicitly set to false). */
  visibleStaticColumnIds: ReadonlyArray<TreeStaticColumnId>;
  /** Metadata fields that are currently visible; opt-in (hidden by default, visible when set to true). */
  visibleMetaColumns: ReadonlyArray<MetaField>;
  /** Precomputed metadata display strings keyed by composite row cache key, then by MetaField apiId. */
  precomputedMetadata: Map<string, Map<string, string>>;
  /** Precomputed cage name display strings keyed by composite row cache key. Only populated when cage_name column is visible. */
  precomputedCageNames: Map<string, string>;
  toggleRoot: (key: RootKey) => void;
  toggleLevel1: (rootKey: RootKey, level1KeyRaw: string) => void;
  toggleLevel2: (rootKey: RootKey, level1KeyRaw: string, level2KeyRaw: string) => void;
  selectedReservedSampleIds: Set<ReservedSampleApiId>;

  toggleReservedSampleIds: (ids: Array<ReservedSampleApiId>) => void;
};

/**
 * The state the three reserved-sample execute steps pass to one another through
 * `location.state`. There is no wizard context or store slice, so every field a later step needs has
 * to be carried forward by the step before it.
 */
export interface ExecuteReservedSamplesLocationState {
  studyApiId?: StudyApiId;
  reservedSampleApiIds?: Array<ReservedSampleApiId>;
  tableSettings?: components['schemas']['UserTableViewSettingsV1'];
  sampleSheetName?: string;
  baseColumns?: Array<SamplingSheetBaseColumn>;
  metadataColumns?: Array<MetadataGlossaryApiId>;
  sortBy?: Array<ReservedSampleSheetSortClause>;
  groups?: Array<GroupTarget>;
  animals?: Array<DosingMatrixAnimal>;
  /**
   * Carried from the collect step rather than refetched: that step already blocks the sheet path on a
   * failed treatments load, so an empty list here can only mean the study genuinely has no treatments.
   */
  treatments?: Array<TreatmentV1>;
}
